import type { FirebaseApp } from 'firebase/app';
import { connectFunctionsEmulator, getFunctions, httpsCallable, type Functions } from 'firebase/functions';
import { collection, doc, onSnapshot, query, serverTimestamp, setDoc, updateDoc, where, type Firestore } from 'firebase/firestore';
import type { EmulatorEndpoint } from '@taki/runtime-config';

/**
 * WHATSAPP, COTE APP : connecter son numero a Lia, le deconnecter, lire son
 * etat. Le canal des notifications de l'org s'allume dans Parametres >
 * Notifications (moteur de notifications), pas ici.
 *
 * Ce que l'app NE SAIT PAS : le token Meta, le hub, la fenetre de 24 h. Elle
 * parle a trois callables du rayon et lit deux documents que les rules lui
 * ouvrent (`docs/socle/whatsapp-lia.md`).
 *
 * LES CONSTANTES CI-DESSOUS SONT UNE RECOPIE de `functions/src/whatsapp/contrat.ts`
 * (`functions/` ne peut pas dependre d'un `@taki/*`, docs/FUNCTIONS.md).
 * `tests/whatsapp-contrat.test.mjs` lit LES DEUX fichiers et echoue si elles se
 * separent : chaque constante tient sur une ligne pour cela.
 */

/** DOIT valoir `FUNCTIONS_REGION` de `functions/src/runtime.ts`. */
export const WHATSAPP_FUNCTIONS_REGION = 'europe-west1';
export const WHATSAPP_CALLABLE_APPAIRER = 'whatsappAppairer';
export const WHATSAPP_CALLABLE_DELIER = 'whatsappDelier';
export const WHATSAPP_LIENS_COLLECTION = 'whatsappLiens';
/**
 * ANCIEN interrupteur d'org (`orgs/{orgId}/whatsappSettings/default`), retire
 * le 05/10/2026 : le canal WhatsApp des notifications s'allume dans
 * Parametres > Notifications (`canaux.whatsapp.actif`, `notifications.ts`).
 * Plus rien ne l'ecrit ni ne le lit ; les rules le tolerent encore.
 */
export const WHATSAPP_SETTINGS_COLLECTION = 'whatsappSettings';
export const WHATSAPP_NUMERO_LIA = '+352621594641';

/** Le numero tel qu'on l'affiche : les quatre derniers chiffres seulement. */
export function masquerNumero(phone: string): string {
  const chiffres = phone.replace(/\D/g, '');
  return `+${'•'.repeat(Math.max(0, chiffres.length - 4))}${chiffres.slice(-4)}`;
}

/** Le numero Lia tel qu'on le lit : +352 621 594 641. */
export function numeroLiaLisible(numero: string = WHATSAPP_NUMERO_LIA): string {
  const chiffres = numero.replace(/\D/g, '');
  return `+${chiffres.slice(0, 3)} ${chiffres.slice(3).replace(/(\d{3})(?=\d)/g, '$1 ')}`;
}

export interface WhatsappAppairage {
  code: string;
  numeroLia: string;
  /** `https://wa.me/<numero>?text=<code>` : la personne n'a qu'a appuyer sur Envoyer. */
  lienWaMe: string;
  expiresAt: Date;
}

export interface WhatsappLien {
  phone: string;
  numeroMasque: string;
  /** La personne accepte les notifications sur ce numero (le canal de l'org doit aussi etre allume). */
  notifications: boolean;
  linkedAt: Date | null;
}


export type WhatsappErrorReason = 'hub-indisponible' | 'refuse' | 'non-connecte' | 'unknown';

export class WhatsappError extends Error {
  override readonly name = 'WhatsappError';
  readonly reason: WhatsappErrorReason;
  constructor(message: string, reason: WhatsappErrorReason, options?: { cause?: unknown }) {
    super(message);
    this.reason = reason;
    if (options?.cause !== undefined) this.cause = options.cause;
    Object.setPrototypeOf(this, WhatsappError.prototype);
  }
}

function traduire(error: unknown): never {
  const code = (error as { code?: unknown } | null)?.code;
  const message = error instanceof Error ? error.message : String(error);
  if (code === 'functions/unavailable') throw new WhatsappError('Le service WhatsApp n’est pas joignable depuis cet environnement.', 'hub-indisponible', { cause: error });
  if (code === 'functions/unauthenticated') throw new WhatsappError('Connectez-vous pour relier WhatsApp.', 'non-connecte', { cause: error });
  if (code === 'functions/permission-denied' || code === 'functions/failed-precondition') throw new WhatsappError(message.replace(/^Firebase: /, '').replace(/ \(functions\/[a-z-]+\)\.?$/, ''), 'refuse', { cause: error });
  throw new WhatsappError('WhatsApp ne répond pas pour le moment. Réessayez.', 'unknown', { cause: error });
}

export interface WhatsappClient {
  /** Demande un code : le hub le tire, le rayon le garde pour ce compte. Vaut 10 minutes. */
  appairer(): Promise<WhatsappAppairage>;
  /** Retire le numero des deux cotes. Rend vrai si un lien existait. */
  delier(): Promise<boolean>;
  /** Le lien de la personne connectee, en temps reel ; `null` sans lien. */
  observerLien(onValue: (lien: WhatsappLien | null) => void, onError?: (error: unknown) => void): () => void;
  /** L'interrupteur PERSONNEL : « recevoir les notifications sur ce numero ». */
  setNotificationsPersonnelles(phone: string, notifications: boolean): Promise<void>;
}

const emulatorBoundFunctions = new WeakSet<Functions>();

function date(value: unknown): Date | null {
  return value != null && typeof value === 'object' && 'toDate' in value && typeof value.toDate === 'function' ? value.toDate() as Date : null;
}

export function createWhatsappClient(app: FirebaseApp, db: Firestore, orgId: string, getActorId: () => string, emulator?: EmulatorEndpoint): WhatsappClient {
  const functions = getFunctions(app, WHATSAPP_FUNCTIONS_REGION);
  if (emulator && !emulatorBoundFunctions.has(functions)) {
    connectFunctionsEmulator(functions, emulator.host, emulator.port);
    emulatorBoundFunctions.add(functions);
  }
  const appairer = httpsCallable<unknown, { code: string; numeroLia: string; lienWaMe: string; expiresAt: string }>(functions, WHATSAPP_CALLABLE_APPAIRER);
  const delier = httpsCallable<unknown, { delie: boolean }>(functions, WHATSAPP_CALLABLE_DELIER);
  return {
    async appairer() {
      try {
        const { data } = await appairer({});
        return { code: data.code, numeroLia: data.numeroLia, lienWaMe: data.lienWaMe, expiresAt: new Date(data.expiresAt) };
      } catch (error) { traduire(error); }
    },
    async delier() {
      try { return (await delier({})).data.delie; }
      catch (error) { traduire(error); }
    },
    observerLien(onValue, onError) {
      const liens = query(collection(db, WHATSAPP_LIENS_COLLECTION), where('orgId', '==', orgId), where('uid', '==', getActorId()));
      return onSnapshot(liens, (snapshot) => {
        const premier = snapshot.docs[0];
        onValue(premier === undefined ? null : { phone: premier.id, numeroMasque: masquerNumero(premier.id),
          notifications: premier.get('notifications') !== false, linkedAt: date(premier.get('linkedAt')) });
      }, onError);
    },
    async setNotificationsPersonnelles(phone, notifications) {
      await updateDoc(doc(db, WHATSAPP_LIENS_COLLECTION, phone), { notifications });
    },
  };
}
